import { useEffect, useMemo, useState } from 'react';
import type { PresetSummaryDto } from '@scs/schemas';
import { Api } from '../lib/api';

export const CATEGORY_LABEL: Record<PresetSummaryDto['category'], string> = {
  shelf: 'Police',
  base: 'Donji ormarići',
  wall: 'Viseći',
  tall: 'Visoki',
  drawers: 'Ladičari',
  wardrobe: 'Garderoberi',
  living: 'Dnevni boravak',
  kitchen: 'Kuhinja',
  custom: 'Prilagođeno',
};

const SCOPE_LABEL: Record<PresetSummaryDto['scope'], string> = { system: 'Sistemski', workspace: 'Radionica', private: 'Lični' };

export function usePresets(refreshKey: number) {
  const [items, setItems] = useState<PresetSummaryDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancel = false;
    Api.presets()
      .then((p) => !cancel && setItems(p))
      .catch((e) => !cancel && setError((e as Error).message));
    return () => {
      cancel = true;
    };
  }, [refreshKey]);
  return { items, error };
}

export function LibraryPanel({ presets, error, disabled, onInsert }: { presets: PresetSummaryDto[] | null; error: string | null; disabled: boolean; onInsert(p: PresetSummaryDto): void }) {
  const [q, setQ] = useState('');
  const [scope, setScope] = useState<'all' | PresetSummaryDto['scope']>('all');
  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = (presets ?? []).filter(
      (p) =>
        (scope === 'all' || p.scope === scope) &&
        (!needle || p.name.toLowerCase().includes(needle) || p.folder.toLowerCase().includes(needle) || p.tags.some((t) => t.toLowerCase().includes(needle))),
    );
    // Folders first (📁 name), then the rest by category.
    const m = new Map<string, PresetSummaryDto[]>();
    const key = (p: PresetSummaryDto) => (p.folder ? `📁 ${p.folder}` : p.category);
    for (const p of [...filtered].sort((a, b) => Number(!a.folder) - Number(!b.folder) || a.folder.localeCompare(b.folder))) m.set(key(p), [...(m.get(key(p)) ?? []), p]);
    return [...m.entries()];
  }, [presets, q, scope]);

  return (
    <div style={{ padding: 8 }}>
      <input className="input" style={{ width: '100%', marginBottom: 6 }} placeholder="Pretraga preseta…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.stopPropagation()} aria-label="Pretraga preseta" />
      <select className="input" style={{ width: '100%', marginBottom: 8 }} value={scope} onChange={(e) => setScope(e.target.value as typeof scope)} aria-label="Vrsta preseta">
        <option value="all">Svi preseti</option>
        <option value="system">Sistemski</option>
        <option value="workspace">Radionica</option>
        <option value="private">Lični</option>
      </select>
      {error && <p className="error-text">{error}</p>}
      {!presets && !error && <p className="faint">Učitavanje…</p>}
      {groups.map(([cat, list]) => (
        <div key={cat} style={{ marginBottom: 10 }}>
          <h3 style={{ margin: '6px 4px' }}>{CATEGORY_LABEL[cat as PresetSummaryDto['category']] ?? cat}</h3>
          {list.map((p) => (
            <div key={p.id} className="lib-item">
              {p.thumbnailAssetId ? <img src={`/api/assets/${p.thumbnailAssetId}`} alt="" width={40} height={40} /> : <div className="lib-thumb" aria-hidden />}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="lib-name" title={p.description}>
                  {p.name}
                </div>
                <div className="faint" style={{ fontSize: 11 }}>
                  {SCOPE_LABEL[p.scope]} · v{p.latestVersion} · {p.kind === 'assembly' ? 'sklop' : 'parametarski'}
                </div>
              </div>
              <button className="btn sm" disabled={disabled} onClick={() => onInsert(p)} title={`Ubaci: ${p.name}`}>
                +
              </button>
            </div>
          ))}
        </div>
      ))}
      {presets && groups.length === 0 && <p className="faint">Nema preseta.</p>}
    </div>
  );
}
